import * as React from 'react';
import { Box, Button, CircularProgress, Grid, TextField, useMediaQuery, useTheme } from '@mui/material';
import { Cached } from '@mui/icons-material';

import { PhoneNumberMask, PhoneVerified } from 'components';
import { formatPhoneNumber } from 'utils';
import { useEmployeeFormManager } from './employee-form.hook';
import { useGenerateEmployeeCodeQuery } from 'store/apis';
import { useAppSelector } from 'store/store.hook';
import { selectCurrentUserInfo } from 'store/slices/auth.slice';

interface EmployeeFormProps {
  employeeId?: number | null;
  onInit?: (loading: boolean) => void;
  onSubmitting?: (loading: boolean) => void;
  next?: (data?: any) => void;
}

export const EmployeeForm = React.forwardRef(
  ({ employeeId, onInit = () => {}, onSubmitting = () => {}, next }: EmployeeFormProps, ref) => {
    const theme = useTheme();
    const down_sm = useMediaQuery(theme.breakpoints.down('sm'));
    const [{ employee, formManager, loading_form, loading_submit }] = useEmployeeFormManager({ employeeId, next });

    React.useImperativeHandle(ref, () => ({ onSubmit: () => formManager.handleSubmit() }), []);
    React.useEffect(() => onInit(loading_form), [loading_form]);
    React.useEffect(() => onSubmitting(loading_submit), [loading_submit]);

    const { orgSlug } = useAppSelector(selectCurrentUserInfo);
    const [skipGenerateEmployeeCode, setSkipGenerateEmployeeCode] = React.useState<boolean>(true);
    const { data, isLoading: employeeCodeGenerating } = useGenerateEmployeeCodeQuery(
      { orgSlug },
      { skip: skipGenerateEmployeeCode },
    );

    React.useEffect(() => {
      if (!employeeCodeGenerating) {
        if (data?.code) {
          formManager.setFieldValue('code', data.code || '');
        }
        setSkipGenerateEmployeeCode(true);
      }
    }, [employeeCodeGenerating, data]);

    return (
      <form onSubmit={formManager.handleSubmit}>
        <Box paddingY={2}>
          <Grid container rowSpacing={4} columnSpacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Name"
                variant="outlined"
                fullWidth
                value={formManager.values.name}
                onChange={formManager.handleChange('name')}
                error={!!(formManager.errors.name && formManager.touched.name)}
                helperText={formManager.touched.name ? formManager.errors.name : null}
              />
            </Grid>

            <Grid item sx={down_sm ? { flex: 1 } : { width: '50%' }}>
              <TextField
                label="Employee Code"
                variant="outlined"
                fullWidth
                disabled
                value={formManager.values.code}
                onChange={formManager.handleChange('code')}
                error={!!(formManager.errors.code && formManager.touched.code)}
                helperText={formManager.touched.code ? formManager.errors.code : null}
              />
            </Grid>

            <Grid item sx={down_sm ? {} : { width: '50%' }}>
              <Button
                startIcon={
                  employeeCodeGenerating ? (
                    <CircularProgress
                      color="inherit"
                      size={14}
                      sx={{
                        marginRight: down_sm ? 0 : 0.8,
                      }}
                    />
                  ) : (
                    <Cached />
                  )
                }
                sx={{
                  paddingX: down_sm ? 0 : 2,
                  marginTop: 1.4,
                  ...(down_sm ? { width: 30, height: 30, minWidth: 30, paddingLeft: 1.25 } : {}),
                }}
                onClick={() => {
                  setSkipGenerateEmployeeCode((prev) => !prev);
                }}
              >
                {down_sm ? '' : 'Generate Code'}
              </Button>
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Email"
                variant="outlined"
                fullWidth
                value={formManager.values.email}
                onChange={formManager.handleChange('email')}
                error={!!(formManager.errors.email && formManager.touched.email)}
                helperText={formManager.touched.email ? formManager.errors.email : null}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Phone Number"
                placeholder="(000) 000-0000"
                variant="outlined"
                fullWidth
                value={formatPhoneNumber(formManager.values.phone)}
                name="phone"
                InputProps={{
                  inputComponent: PhoneNumberMask as any,
                }}
                onChange={formManager.handleChange('phone')}
                error={!!(formManager.errors.phone && formManager.touched.phone)}
                helperText={formManager.touched.phone ? formManager.errors.phone : null}
              />
              {employee && employee.phone_verified_at === null && <PhoneVerified user={employee} />}
            </Grid>
          </Grid>
        </Box>
      </form>
    );
  },
);
